import { isVerticalAppRoute } from '@common/urlBasePath';
import { useEffect } from 'react';

import {
  META_PIXEL_ID,
  OAUTH_SIGNUP_FLAG_KEY,
  OPENAI_PIXEL_ID,
  allowedDomain,
  allowedMetaPixelDomains,
  allowedOpenAIPixelDomains
} from '../../common/auth/constant';
import { GlobalWindow, MetaPixelFbq, OpenAIPixelOaiq } from '../../utils/types';
import { getSignupFormHiddenFields } from './common';

export function useHubSpot() {
  useEffect(() => {
    if (!allowedDomain.includes(window.location.host)) return;
    const script = document.createElement('script');
    script.src = 'https://js.hs-scripts.com/22397615.js';
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);
    return () => {
      script.remove();
    };
  }, []);
}

export function useMetaPixel() {
  useEffect(() => {
    if (!allowedMetaPixelDomains.includes(window.location.host)) return;
    const globalWindow = window as unknown as GlobalWindow;

    // Prevent injecting multiple times
    if (globalWindow.fbq) return;
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue.push(args);
      }
    } as MetaPixelFbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    globalWindow.fbq = fbq;
    if (!globalWindow._fbq) globalWindow._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.getElementsByTagName('head')[0].appendChild(script);
    // Initialize and track initial page view
    fbq('init', META_PIXEL_ID);
    fbq('track', 'PageView');
    return () => {
      script.remove();
      delete globalWindow.fbq;
      delete globalWindow._fbq;
    };
  }, []);
}

const loadOpenAIPixel = () => {
  if (!allowedOpenAIPixelDomains.includes(window.location.host) || !isVerticalAppRoute()) return;
  const globalWindow = window as unknown as GlobalWindow;

  if (globalWindow.oaiq) return;
  const oaiq = function (...args: unknown[]) {
    oaiq.q.push(args);
  } as OpenAIPixelOaiq;
  oaiq.q = [];
  globalWindow.oaiq = oaiq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
  document.getElementsByTagName('head')[0].appendChild(script);
  oaiq('init', { pixelId: OPENAI_PIXEL_ID });
  return () => {
    script.remove();
    delete globalWindow.oaiq;
  };
};

/**
 * Mount on the signup page only: the pixel auto-collects hashed emails/names from any form on the page,
 * so it must not run on logged-in pages where users type other people's emails.
 */
export function useOpenAIPixel() {
  useEffect(loadOpenAIPixel, []);
}

export const trackOpenAIPixelSignup = () => {
  (window as unknown as GlobalWindow).oaiq?.('measure', 'registration_completed', { type: 'customer_action' });
};

/**
 * Reports a successful Google OAuth sign-up to HubSpot (creates the contact) and the OpenAI pixel.
 */
export function useOAuthSignupTracking(user?: { email?: string; created?: number }) {
  useEffect(() => {
    if (!user?.email || !user?.created) return;

    // Only act for accounts created within the last 10 minutes (same threshold as isNewUser)
    const isNewUser = user.created >= Date.now() - 1000 * 60 * 10;
    if (!isNewUser) {
      // User already existed — clear the flag so it doesn't leak into later sessions
      localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);
      return;
    }

    if (localStorage.getItem(OAUTH_SIGNUP_FLAG_KEY) !== 'true') return;

    // Clear immediately to prevent duplicate submissions on re-renders
    localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);

    const globalWindow = window as unknown as GlobalWindow;
    const _hsq = (globalWindow._hsq = globalWindow._hsq ?? []);
    // Same hidden inputs as SignUpForm (UTM / ad params + usersignupdate)
    _hsq.push(['identify', { email: user.email, ...getSignupFormHiddenFields() }]);
    _hsq.push(['trackPageView']);
    loadOpenAIPixel();
    trackOpenAIPixelSignup();
  }, [user?.email, user?.created]);
}
